'use client';

import * as EngineModule from '@gnsx/genesys.js';
import { getDefaultObject } from '@gnsx/genesys.js';
import { isFunction, isPlainObject } from 'es-toolkit';
import { get, invoke, set } from 'es-toolkit/compat';
import { getParentOfType, hasParentOfType, isAlive } from 'mobx-state-tree';
import * as THREE from 'three';

import * as editorMeta from '../core/editorClassMeta';
import { getPropertyMetadataArrayMeta, getPropertyMetadataClassOrAssetType, getPropertyMetadataIntegerMeta, getPropertyMetadataMultiSelectMeta, getPropertyMetadataNumberMeta, getPropertyMetadataSelectOptions } from '../core/editorClassMeta';
import { UndoRedoActionType } from '../core/undoRedo/undoRedoTypes';

import * as M from './properties';
import { PropertyType } from './propertyType';


import type { IPropertySource, IPropertyStore } from './propertyStore';
import type { IProperty, SelectOptionsTypes, ValueChangeContext, ValueChangeHandler } from './types';
import type { PropertyMetadata } from '@gnsx/genesys.js';
import type * as ENGINE from '@gnsx/genesys.js';
import type { Instance } from 'mobx-state-tree';


import { populateGeometryControls } from '@/app/editor/property-editor/controls/object-controls/geometryControls';
import { classHelpers } from '@/editor/core';
import { objectActions } from '@/editor/core';
import { AssetType } from '@/editor/core/assetTypes';
import { displayCase } from '@/editor/core/nameHelpers';
import { canRecordPrefabEditorUndo } from '@/editor/core/undoRedo';
import { createAccessorFromTarget, setPropertyValueDirect } from '@/editor/core/universalAccessor';
import { editorStore } from '@/editor/store/editorStore';
import { EditableObject } from '@/editor/store/models';

const DEBUG_LOG = {
  defaultSetPropertyValue: false,
  assetDefaultValue: false,
  setPropertyValue: false,
  createProperty: false,
  propertyOrder: false,
} as const;

/**
 * Map metadata asset type names to editor AssetType enum values
 * Metadata uses names like 'modelPath', 'texturePath', etc.
 * Editor uses 'model', 'texture', etc.
 */
function mapMetadataTypeToAssetType(metadataType: string): AssetType | undefined {
  const typeMap: Record<string, AssetType> = {
    'modelPath': AssetType.Model,
    'texture': AssetType.Texture,
    'texturePath': AssetType.Texture,
    'hdriPath': AssetType.HDRI,
    'audioPath': AssetType.Audio,
    'videoPath': AssetType.Video,
    'scenePath': AssetType.Scene,
    'prefabPath': AssetType.Prefab,
    'materialPath': AssetType.Material,
    'jsonPath': AssetType.Json,
    'sourcecodePath': AssetType.SourceCode,
    'jsclassPath': AssetType.JsClass,
    'resource': AssetType.Resource,
    'vfxPath': AssetType.VFX,
    'voxelPath': AssetType.Voxel,
  };
  return typeMap[metadataType];
}

/**
 * Extract nested template value from a template object using a property path
 */
function extractNestedTemplateValue(templateValue: any, childPath: string, parentPath: string): any {
  if (!templateValue || typeof templateValue !== 'object') {
    return undefined;
  }

  // Calculate the relative path from parent to child
  const relativePath = parentPath ? childPath.replace(`${parentPath}.`, '') : childPath;

  // Handle array indices in path (e.g., "items[0]" -> ["items", "0"])
  const pathParts = relativePath.split(/[.\[\]]+/).filter(Boolean);

  let current = templateValue;
  for (const part of pathParts) {
    if (current === null || current === undefined) {
      return undefined;
    }

    // Handle numeric indices for arrays
    const index = parseInt(part, 10);
    if (!isNaN(index) && Array.isArray(current)) {
      current = current[index];
    } else {
      current = current[part];
    }
  }

  if (DEBUG_LOG.createProperty && current !== undefined) {
    console.log('🔍 [PropertyEditor] Extracted nested template value', {
      childPath,
      parentPath,
      relativePath,
      pathParts,
      extractedValue: current
    });
  }

  return current;
}


export interface PropertyContext {
  path: string;
  parentPath: string;
  label?: string; // If not provided will try find from meta else use path name
  labelPrefix?: string;
  meta?: PropertyMetadata; // Use PropertyMetadata for the unified system
  store: IPropertyStore;
  sources?: IPropertySource[]; // Subobject sources, use store sources if not provided
  order?: number; // Display order for the property
}
export interface PropertyTypeResult {
  type: PropertyType | undefined;
  editInline?: string;
  assetTypes?: AssetType[];
  resourceType?: string;
  selectOptions?: SelectOptionsTypes;
}

/**
 * resolvePropertyType
 */
export function resolvePropertyType(context: PropertyContext, value?: any): PropertyTypeResult {
  // Check for multiselect property first - treat as multiselect if metadata present, regardless of value
  const multiSelectMeta = getPropertyMetadataMultiSelectMeta(context.meta);
  if (multiSelectMeta) {
    return {
      type: PropertyType.MultiSelect,
      selectOptions: multiSelectMeta.options
    };
  }

  // Check metadata for enum (single select)
  const selectOptions = getPropertyMetadataSelectOptions(context.meta) as string[] | Record<string, string>;
  if (selectOptions) return { type: PropertyType.Select, selectOptions };

  // Check for custom selector
  const selector = (context.meta as any)?.selector;
  if (selector) {
    const store = context.store;
    const rootTarget = store.targets[0];
    const parentTarget = context.parentPath ? get(rootTarget, context.parentPath) : rootTarget;
    const target = get(rootTarget, context.path);

    let options: string[] | Record<string, string> | undefined;
    if (typeof selector === 'function') {
      try {
        options = selector(target, rootTarget, store);
      } catch (e) {
        console.error(`[PropertyEditor] Selector function failed for ${context.path}`, e);
      }
    } else if (typeof selector === 'string') {
      // Find method on target, parent, or root
      const findMethod = (obj: any, name: string) => (obj && typeof obj[name] === 'function') ? obj[name].bind(obj) : undefined;
      const method = findMethod(target, selector) || findMethod(parentTarget, selector) || findMethod(rootTarget, selector);

      if (method) {
        try {
          options = method();
        } catch (e) {
          console.error(`[PropertyEditor] Selector method "${selector}" failed for ${context.path}`, e);
        }
      }
    }

    if (options) {
      return {
        type: PropertyType.Select,
        selectOptions: options
      };
    }
  }

  // Check for reference types (actorReference, componentReference, spawnable)
  if ((context.meta as any)?.type === 'actorReference') {
    return { type: PropertyType.ActorReference };
  }
  if ((context.meta as any)?.type === 'componentReference') {
    return { type: PropertyType.ComponentReference };
  }
  if ((context.meta as any)?.type === 'spawnable') {
    return { type: PropertyType.Spawnable };
  }

  const type = getPropertyMetadataClassOrAssetType(context.meta);
  if (typeof type === 'string' || Array.isArray(type)) {
    // Convert metadata asset types to editor AssetType enum values
    const metadataTypes = Array.isArray(type) ? type : [type];
    const assetTypes = metadataTypes
      .map(t => mapMetadataTypeToAssetType(t))
      .filter((t): t is AssetType => t !== undefined);

    if (assetTypes.length > 0) {
      const resourceType = (context.meta as { resourceType?: string })?.resourceType;
      return { type: PropertyType.Asset, assetTypes, resourceType };
    }
  }

  // Handle class types from metadata
  if (isFunction(type)) {
    const classType = type as Function;
    if (classHelpers.isSubclass(classType, THREE.Color)) return { type: PropertyType.Color };
    if (classHelpers.isSubclass(classType, THREE.Vector2)) return { type: PropertyType.Vector2 };
    if (classHelpers.isSubclass(classType, THREE.Vector3)) return { type: PropertyType.Vector3 };
    if (classHelpers.isSubclass(classType, THREE.Vector4)) return { type: PropertyType.Vector4 };
    if (classHelpers.isSubclass(classType, THREE.Euler)) return { type: PropertyType.Euler };
    if (classHelpers.isSubclass(classType, THREE.Texture)) return { type: PropertyType.Asset, assetTypes: [AssetType.Texture] };
    if (classHelpers.isSubclass(classType, THREE.Material)) return { type: PropertyType.Asset, assetTypes: [AssetType.Material] };
  }

  // Check for array metadata - if present, treat as array even if value is undefined
  const arrayMeta = getPropertyMetadataArrayMeta(context.meta);
  if (arrayMeta !== undefined) {
    return { type: PropertyType.Array };
  }

  // Check for primitive type metadata - use metadata type even if value is undefined
  // This ensures properties in options are always rendered even without a value
  if (context.meta?.type) {
    const metadataTypeMap: Record<string, PropertyType> = {
      'string': PropertyType.String,
      'number': PropertyType.Number,
      'boolean': PropertyType.Boolean,
      'color': PropertyType.Color,
      'euler': PropertyType.Euler,
      'object': PropertyType.Object,
    };

    if (context.meta.type in metadataTypeMap) {
      return { type: metadataTypeMap[context.meta.type] };
    }

    // Handle texture type from metadata even when value is null
    if (context.meta.type === 'texture') {
      return { type: PropertyType.Asset, assetTypes: [AssetType.Texture] };
    }
  }

  // Handle primitive types from value
  const primitiveMap = {
    'number': PropertyType.Number,
    'boolean': PropertyType.Boolean,
    'string': PropertyType.String,
    'undefined': undefined,
    'null': undefined,
  };
  const valueType = typeof value;
  if (valueType in primitiveMap) {
    return { type: primitiveMap[valueType as keyof typeof primitiveMap] };
  }

  // Handle containers
  if (isPlainObject(value)) return { type: PropertyType.Object };
  if (Array.isArray(value)) return { type: PropertyType.Array };

  // Handle class instances
  if (value instanceof THREE.Color) return { type: PropertyType.Color };
  if (value instanceof THREE.Vector2) return { type: PropertyType.Vector2 };
  if (value instanceof THREE.Vector3) return { type: PropertyType.Vector3 };
  if (value instanceof THREE.Vector4) return { type: PropertyType.Vector4 };
  if (value instanceof THREE.Euler) return { type: PropertyType.Euler };
  if (value instanceof THREE.Texture) return { type: PropertyType.Asset, assetTypes: [AssetType.Texture] };
  if (value instanceof THREE.Material) return { type: PropertyType.Asset, assetTypes: [AssetType.Material] };

  if (value instanceof THREE.BufferGeometry) return { type: PropertyType.Object, editInline: 'GeometryControls' };

  // FALLBACK: For option properties without metadata or value, default to String
  // This ensures all constructor options are visible even if they lack metadata
  const isOptionProperty = context.path.startsWith('options.');
  if (isOptionProperty && (value === undefined || value === null)) {
    if (DEBUG_LOG.createProperty) {
      console.log('⚠️ [PropertyEditor] Using String fallback for option property without metadata', {
        path: context.path,
        hasMeta: !!context.meta,
        value
      });
    }
    return { type: PropertyType.String };
  }

  return { type: undefined };
}


/** Interface for createProperty function parameters */
export interface CreatePropertyParams {
  /** Property context containing path, metadata and store */
  context: PropertyContext
  /** Value to create property for */
  value: any
  /** Optional handler called when property value changes */
  onChange?: ValueChangeHandler
  /** Optional handler called when property is created */
  onCreate?: (property: IProperty) => void
  /** Optional depth level for nested properties */
  depth?: number
  /** Whether to skip the default onChange handler */
  isSkipDefaultOnChange?: boolean
  /** Whether to disable the property */
  disabled?: boolean
  /** Optional template value for override detection */
  templateValue?: any
  /** Whether this property is an override (separate from templateValue to handle undefined as valid template) */
  isOverride?: boolean
}
/** Generate an error message for an invalid property */
function getErrorMessageForInvalidProperty(value: any, meta: PropertyMetadata | undefined): string {
  if (value === null) {
    return 'Cannot resolve property type: value is null. Add type metadata (e.g., @property({type: \'object\', fields: {...}})) or provide a non-null default value.';
  }
  if (value === undefined) {
    return 'Cannot resolve property type: value is undefined. Add type metadata or provide a default value.';
  }
  if (typeof value === 'function') {
    return 'Cannot display function properties in the editor.';
  }
  if ((meta as any)?.type === 'unknown') {
    return 'This property type is not supported';
  }
  if (meta?.type) {
    return `Unknown metadata type: "${meta.type}". Supported types: string, number, boolean, color, object, array.`;
  }
  return `Cannot resolve property type for value of type "${typeof value}".`;
}

/**
 * Create property based on value and context, default handler is used if no onChange handler is provided.
 */
export function createProperty({ context, value, onChange, onCreate, depth = 0, isSkipDefaultOnChange = false, disabled = false, templateValue, isOverride = false }: CreatePropertyParams): IProperty | undefined {
  const result = resolvePropertyType(context, value);

  // Get property target
  const { path, parentPath, label: contextLabel, labelPrefix, meta, store, sources = store.sources, order } = context;
  const target = sources?.[0]?.target;
  const uuid = get(target, 'uuid');
  const key = `${uuid ? `${uuid}.` : ''}${path}`;
  let label = contextLabel ?? displayCase(path.split('.').pop() ?? '');
  if (labelPrefix && !label.startsWith(labelPrefix)) label = `${labelPrefix} ${label}`;

  const propertyName = path.split('.').pop() ?? '';
  const description = meta?.description;
  const tooltip = description ? `${propertyName}: ${description}` : propertyName;

  // If type couldn't be resolved, return an ErrorProperty to inform the user
  if (!result.type) {
    const errorMessage = getErrorMessageForInvalidProperty(value, meta);

    // Determine the error type based on the value
    let errorType = M.ErrorPropertyType.Unknown;
    if (value === null) {
      errorType = M.ErrorPropertyType.NullValue;
    } else if (value === undefined) {
      errorType = M.ErrorPropertyType.Unknown;
    } else if (typeof value === 'function' || (meta as any)?.type === 'unknown') {
      errorType = M.ErrorPropertyType.InvalidType;
    } else if (!meta || !meta.type) {
      errorType = M.ErrorPropertyType.MissingMetadata;
    }

    const errorProperty = M.ErrorProperty.create({
      type: PropertyType.Error,
      errorType,
      key,
      path,
      parentPath,
      label,
      tooltip,
      errorMessage,
      valueType: value === null ? 'null' : typeof value,
      rawValue: value === null ? 'null' : (typeof value === 'object' ? JSON.stringify(value) : String(value)).slice(0, 100),
    });
    errorProperty.setStore(store);
    onCreate?.(errorProperty);
    return errorProperty;
  }

  const readOnly = meta?.readonly ?? false;
  const params = { key, path, parentPath, label, meta, disabled, readOnly, ...(tooltip && { tooltip }), ...(order !== undefined && { order }) };

  if (DEBUG_LOG.propertyOrder && order !== undefined) {
    console.log('🔢 [PropertyEditor] Creating property with order', { path, order, label });
  }

  const setupProperty = (property: IProperty, onChange?: ValueChangeHandler, onCreate?: (property: IProperty) => void, overrides: { overrideIsSkipDefaultOnChange?: boolean } = {}) => {
    property.setStore(store);

    onCreate?.(property);

    const useDefaultOnChange = !(overrides?.overrideIsSkipDefaultOnChange ?? isSkipDefaultOnChange);
    if (useDefaultOnChange) {
      property.on('value', defaultOnChange);
    }

    if (onChange) {
      property.on('value', onChange);
    }
    return property;
  };

  if (DEBUG_LOG.createProperty && isOverride) {
    console.log('🎯 [PropertyEditor] Creating property with template value', { path, templateValue, valueType: typeof templateValue, isOverride });
  }

  // Helper function to get default value for a property
  const getDefaultValue = (metadataDefaultValue: any, fallbackDefault: any) => {
    // Priority: templateValue (prefab) > CDO value > metadata.default (fallback) > fallback
    if (templateValue !== undefined) return templateValue;

    // Try to get the default value from the CDO registry
    if (target) {
      const cdo = getDefaultObject(target);
      if (cdo) {
        // Use get() to support nested paths like "options.maxSpeed"
        const cdoValue = get(cdo, path);
        if (cdoValue !== undefined) {
          return cdoValue;
        }
      }
    }

    // Fallback to metadata.default if CDO doesn't have the value
    if (meta?.default !== undefined) return meta.default;
    return fallbackDefault;
  };

  try {
    switch (result.type) {
      case PropertyType.Number: {
        const defaultValue = getDefaultValue(templateValue, value ?? 0);
        const defaults = { defaultValue, isOverride };
        const property = M.NumberProperty.create({ type: PropertyType.Number, value, ...defaults, ...params });

        // Apply number/integer metadata configuration
        const numberMeta = getPropertyMetadataNumberMeta(context.meta);
        const integerMeta = getPropertyMetadataIntegerMeta(context.meta);

        if (numberMeta) {
          if (numberMeta.min !== undefined) property.setMin(numberMeta.min);
          if (numberMeta.max !== undefined) property.setMax(numberMeta.max);
          if (numberMeta.step !== undefined) property.setStep(numberMeta.step);
          if (numberMeta.decimals !== undefined) property.setDecimalPlaces(numberMeta.decimals);
          if (numberMeta.unit !== undefined) property.setUnit(numberMeta.unit);
        } else if (integerMeta) {
          // For integer metadata, use decimals: 0 to ensure integer behavior
          if (integerMeta.min !== undefined) property.setMin(integerMeta.min);
          if (integerMeta.max !== undefined) property.setMax(integerMeta.max);
          if (integerMeta.step !== undefined) property.setStep(integerMeta.step);
          if (integerMeta.unit !== undefined) property.setUnit(integerMeta.unit);
          property.setDecimalPlaces(0); // Force integer behavior
        }

        return setupProperty(property, onChange, onCreate);
      }
      case PropertyType.String: {
        const defaultValue = getDefaultValue(templateValue, value ?? '');
        const defaults = { defaultValue, isOverride };
        const property = M.StringProperty.create({ type: PropertyType.String, value, ...defaults, ...params });
        return setupProperty(property, onChange, onCreate);
      }
      case PropertyType.Boolean: {
        const defaultValue = getDefaultValue(templateValue, value ?? false);
        const defaults = { defaultValue, isOverride };
        const property = M.BooleanProperty.create({ type: PropertyType.Boolean, value, ...defaults, ...params });
        return setupProperty(property, onChange, onCreate);
      }
      case PropertyType.Select: {
        const defaults = { isOverride };
        const selectProperty = M.SelectProperty.create({ type: PropertyType.Select, value, ...defaults, ...params });
        if (result.selectOptions) {
          selectProperty.setOptions(result.selectOptions);
          // Set defaultValue AFTER options are available to avoid validation issues
          // Priority: templateValue (prefab) > CDO value > metadata.default > first option > current value
          let defaultVal: any;
          if (templateValue !== undefined) {
            defaultVal = templateValue;
          } else if (target) {
            const cdo = getDefaultObject(target);
            if (cdo) {
              const cdoValue = get(cdo, path);
              if (cdoValue !== undefined) {
                defaultVal = cdoValue;
              }
            }
          }

          if (defaultVal === undefined && meta?.default !== undefined) {
            defaultVal = meta.default;
          } else if (defaultVal === undefined) {
            if (Array.isArray(result.selectOptions) && result.selectOptions.length > 0) {
              defaultVal = result.selectOptions[0];
            } else if (typeof result.selectOptions === 'object') {
              const keys = Object.keys(result.selectOptions);
              defaultVal = keys.length > 0 ? keys[0] : value;
            } else {
              defaultVal = value;
            }
          }

          if (defaultVal !== undefined && defaultVal !== '') {
            selectProperty.setDefaultValue(defaultVal);
          }
        }
        return setupProperty(selectProperty, onChange, onCreate);
      }
      case PropertyType.MultiSelect: {
        const multiSelectValue = Array.isArray(value) ? value.filter(v => v != null).map(v => String(v)) : [];
        const templateMultiSelectValue = Array.isArray(templateValue) ? templateValue.filter(v => v != null).map(v => String(v)) : [];
        const metadataDefaultValue = meta?.default !== undefined && Array.isArray(meta.default)
          ? meta.default.filter(v => v != null).map(v => String(v))
          : [];

        // Extract multiselect metadata
        const multiSelectMeta = getPropertyMetadataMultiSelectMeta(context.meta);

        const selectOptions = result.selectOptions ?? multiSelectMeta?.options;

        if (DEBUG_LOG.createProperty) {
          console.log('🔷 [PropertyEditor] Creating MultiSelect property', {
            path: context.path,
            multiSelectMeta,
            resultSelectOptions: result.selectOptions,
            finalSelectOptions: selectOptions,
            value: multiSelectValue,
            creatable: multiSelectMeta?.creatable ?? false
          });
        }

        const defaults = { isOverride };
        const multiSelectProperty = M.MultiSelectPropertyBuilder.create({
          type: PropertyType.MultiSelect,
          value: multiSelectValue,
          placeholder: multiSelectMeta?.placeholder ?? 'Select...',
          maxSelected: multiSelectMeta?.maxSelected,
          creatable: multiSelectMeta?.creatable ?? false,
          useDisplayCase: multiSelectMeta?.useDisplayCase ?? true,
          selectOptions: selectOptions,
          ...defaults,
          ...params
        });

        if (DEBUG_LOG.createProperty) {
          console.log('🔷 [PropertyEditor] MultiSelect property created', {
            path: context.path,
            optionsCount: multiSelectProperty.options.length,
            options: multiSelectProperty.options.slice(),
            creatable: multiSelectProperty.creatable
          });
        }

        // Set defaultValue AFTER options are available to avoid validation issues
        // Priority: templateValue (prefab) > CDO value > metadata.default > empty array
        let defaultMultiSelectValue: string[];
        if (templateValue !== undefined && templateMultiSelectValue.length > 0) {
          defaultMultiSelectValue = templateMultiSelectValue;
        } else if (target) {
          const cdo = getDefaultObject(target);
          if (cdo) {
            const cdoValue = get(cdo, path);
            if (Array.isArray(cdoValue)) {
              defaultMultiSelectValue = cdoValue.filter(v => v != null).map(v => String(v));
            } else {
              defaultMultiSelectValue = metadataDefaultValue.length > 0 ? metadataDefaultValue : [];
            }
          } else {
            defaultMultiSelectValue = metadataDefaultValue.length > 0 ? metadataDefaultValue : [];
          }
        } else if (metadataDefaultValue.length > 0) {
          defaultMultiSelectValue = metadataDefaultValue;
        } else {
          defaultMultiSelectValue = [];
        }
        multiSelectProperty.setDefaultValue(defaultMultiSelectValue);
        return setupProperty(multiSelectProperty, onChange, onCreate);
      }
      case PropertyType.Color: {
        const color = value instanceof THREE.Color ? value : new THREE.Color(value ?? '#ffffff');
        // For color, priority: templateValue (prefab) > CDO value > metadata.default > current value
        let defaultColor: THREE.Color;
        if (templateValue !== undefined) {
          defaultColor = templateValue instanceof THREE.Color ? templateValue : new THREE.Color(templateValue);
        } else if (target) {
          const cdo = getDefaultObject(target);
          if (cdo) {
            const cdoValue = get(cdo, path);
            if (cdoValue instanceof THREE.Color) {
              defaultColor = cdoValue;
            } else if (cdoValue !== null && cdoValue !== undefined && typeof cdoValue !== 'boolean') {
              defaultColor = new THREE.Color(cdoValue);
            } else {
              const metaDefault = meta?.default;
              if (metaDefault !== undefined && metaDefault !== null && typeof metaDefault !== 'boolean') {
                // Check if it's already a Color object by checking for isColor property
                if (typeof metaDefault === 'object' && (metaDefault as any).isColor) {
                  defaultColor = metaDefault as THREE.Color;
                } else {
                  defaultColor = new THREE.Color(metaDefault);
                }
              } else {
                defaultColor = color.clone();
              }
            }
          } else {
            const metaDefault = meta?.default;
            if (metaDefault !== undefined && metaDefault !== null && typeof metaDefault !== 'boolean') {
              // Check if it's already a Color object by checking for isColor property
              if (typeof metaDefault === 'object' && (metaDefault as any).isColor) {
                defaultColor = metaDefault as THREE.Color;
              } else {
                defaultColor = new THREE.Color(metaDefault);
              }
            } else {
              defaultColor = color.clone();
            }
          }
        } else {
          const metaDefault = meta?.default;
          if (metaDefault !== undefined && metaDefault !== null && typeof metaDefault !== 'boolean') {
            // Check if it's already a Color object by checking for isColor property
            if (typeof metaDefault === 'object' && (metaDefault as any).isColor) {
              defaultColor = metaDefault as THREE.Color;
            } else {
              defaultColor = new THREE.Color(metaDefault);
            }
          } else {
            defaultColor = color.clone();
          }
        }
        const defaults = { defaultValue: defaultColor, isOverride };
        const property = M.ColorProperty.create({ type: PropertyType.Color, value: color, ...defaults, ...params });
        return setupProperty(property, onChange, onCreate);
      }
      case PropertyType.Vector2: {
        const defaultValue = templateValue ?? (target ? (get(getDefaultObject(target), path) ?? (meta?.default !== undefined ? meta.default : value)) : (meta?.default !== undefined ? meta.default : value));
        const defaults = { defaultValue, isOverride };
        const vecMeta2 = meta as any;
        const property = M.Vector2Property.create({ type: PropertyType.Vector2, value, ...defaults, ...params, ...(vecMeta2?.showLinking !== undefined && { showLinking: vecMeta2.showLinking }) });
        if (vecMeta2?.step !== undefined) property.setStep(vecMeta2.step);
        if (vecMeta2?.decimals !== undefined) property.setDecimalPlaces(vecMeta2.decimals);
        if (vecMeta2?.min !== undefined) property.setMin(vecMeta2.min);
        if (vecMeta2?.max !== undefined) property.setMax(vecMeta2.max);
        return setupProperty(property, onChange, onCreate);
      }
      case PropertyType.Vector3: {
        const defaultValue = templateValue ?? (target ? (get(getDefaultObject(target), path) ?? (meta?.default !== undefined ? meta.default : value)) : (meta?.default !== undefined ? meta.default : value));
        const defaults = { defaultValue, isOverride };
        const vecMeta3 = meta as Extract<typeof meta, { type?: 'vector3' }>;
        const property = M.Vector3Property.create({ type: PropertyType.Vector3, value, ...defaults, ...params, ...(vecMeta3?.showLinking !== undefined && { showLinking: vecMeta3.showLinking }) });
        if (vecMeta3?.step !== undefined) property.setStep(vecMeta3.step);
        if (vecMeta3?.decimals !== undefined) property.setDecimalPlaces(vecMeta3.decimals);
        if (vecMeta3?.min !== undefined) property.setMin(vecMeta3.min);
        if (vecMeta3?.max !== undefined) property.setMax(vecMeta3.max);
        return setupProperty(property, onChange, onCreate);
      }
      case PropertyType.Euler: {
        // Engine stores Euler in RADIANS. The EulerProperty model converts to/from degrees internally.
        // `value` here is a THREE.Euler (radians) from the engine.
        // We pass it to EulerProperty.create() as the raw snapshot; the preProcessor handles it.
        const rawEuler = value instanceof THREE.Euler ? value : new THREE.Euler(0, 0, 0);
        // Convert to degrees for the initial display value stored in the model
        const degValue = new THREE.Vector3(
          THREE.MathUtils.radToDeg(rawEuler.x),
          THREE.MathUtils.radToDeg(rawEuler.y),
          THREE.MathUtils.radToDeg(rawEuler.z),
        );
        // Determine default value (also in degrees)
        let rawDefault: THREE.Euler;
        if (templateValue instanceof THREE.Euler) {
          rawDefault = templateValue;
        } else if (target) {
          const cdoVal = get(getDefaultObject(target), path);
          rawDefault = cdoVal instanceof THREE.Euler ? cdoVal : rawEuler;
        } else {
          rawDefault = rawEuler;
        }
        const degDefault = new THREE.Vector3(
          THREE.MathUtils.radToDeg(rawDefault.x),
          THREE.MathUtils.radToDeg(rawDefault.y),
          THREE.MathUtils.radToDeg(rawDefault.z),
        );
        const property = M.EulerProperty.create({
          type: PropertyType.Euler,
          value: degValue,
          defaultValue: degDefault,
          eulerOrder: rawEuler.order,
          isOverride,
          ...params
        } as any);
        const eulerMeta = meta as Extract<typeof meta, { type?: 'euler' }>;
        property.setStep(eulerMeta?.step ?? 1);
        if (eulerMeta?.decimals !== undefined) property.setDecimalPlaces(eulerMeta.decimals);
        if (eulerMeta?.min !== undefined) property.setMin(eulerMeta.min);
        if (eulerMeta?.max !== undefined) property.setMax(eulerMeta.max);
        // EulerProperty already emits radians via notifyValueChange, so the default handler works.
        return setupProperty(property as unknown as IProperty, onChange, onCreate);
      }
      case PropertyType.Vector4: {
        const defaultValue = templateValue ?? (target ? (get(getDefaultObject(target), path) ?? (meta?.default !== undefined ? meta.default : value)) : (meta?.default !== undefined ? meta.default : value));
        const defaults = { defaultValue, isOverride };
        const vecMeta4 = meta as Extract<typeof meta, { type?: 'vector4' }>;
        const property = M.Vector4Property.create({ type: PropertyType.Vector4, value, ...defaults, ...params, ...(vecMeta4?.showLinking !== undefined && { showLinking: vecMeta4.showLinking }) });
        if (vecMeta4?.step !== undefined) property.setStep(vecMeta4.step);
        if (vecMeta4?.decimals !== undefined) property.setDecimalPlaces(vecMeta4.decimals);
        if (vecMeta4?.min !== undefined) property.setMin(vecMeta4.min);
        if (vecMeta4?.max !== undefined) property.setMax(vecMeta4.max);
        return setupProperty(property, onChange, onCreate);
      }
      case PropertyType.Object: {
        const property = M.ObjectPropertyBuilder.create({
          type: PropertyType.Object,
          ...params,
          value,
          sources,
          store
        });

        // Show inline controls if specified
        if (result.editInline) { // TODO: <AY> generic editInline selection property
          if (result.editInline === 'GeometryControls') {
            setupProperty(property, undefined, onCreate, { overrideIsSkipDefaultOnChange: true });
            property.setValue(value, sources.map(source => ({ editable: source.editable, target: get(source.target, path) })), true, true);
            const updateTargetValue = (_path: string, _value: any, _context: ValueChangeContext) => {
              if (_path === path) {
                const newSources = (_context.property as Instance<typeof M.ObjectProperty>).sources;
                if (newSources) {
                  sources.forEach((source, index) => {
                    if (index < newSources.length) {
                      const newTarget = newSources[index].target;
                      set(source.target, _path, newTarget);
                      objectActions.notifyEditorPropertyChanged(source.target, _path, newTarget);
                    }
                  });
                }
                onChange?.(_path, _value, _context);
              }
            };
            populateGeometryControls(property, true, false, updateTargetValue, templateValue instanceof THREE.BufferGeometry ? templateValue : null);
            return property;
          }
        } // end editInline

        setupProperty(property, onChange, onCreate);

        // Recursively create child properties for object properties
        // Use value keys if available, otherwise use fields from metadata for null/undefined values
        const valueIsObject = isPlainObject(value);
        const metadataFields = meta && 'fields' in meta ? (meta as any).fields as Record<string, PropertyMetadata> : undefined;

        if (valueIsObject || metadataFields) {
          const childProperties: IProperty[] = [];

          // Determine child paths: from value keys or from metadata fields
          const childPaths = valueIsObject
            ? Object.keys(value).filter(childName => !store.excludedPaths.includes(`${path}.${childName}`))
            : metadataFields
              ? Object.keys(metadataFields).filter(childName => !store.excludedPaths.includes(`${path}.${childName}`))
              : [];

          for (const childName of childPaths) {
            const childPath = path ? `${path}.${childName}` : childName;
            const childValue = valueIsObject ? value[childName] : undefined;

            // Get child metadata from either the property metadata lookup or the fields definition
            const childMeta = metadataFields?.[childName]
              ?? editorMeta.getPropertyMeta(meta, childName) as PropertyMetadata | undefined;

            // Skip functions to prevent issues
            if (typeof childValue === 'function') {
              continue;
            }

            // Skip null/undefined values ONLY if we don't have type metadata that can create a property
            // Note: When using metadata fields, we always have type metadata
            const hasTypeMetadata = childMeta && (
              childMeta.type !== undefined ||
              getPropertyMetadataClassOrAssetType(childMeta) !== undefined ||
              getPropertyMetadataSelectOptions(childMeta) !== undefined
            );

            if ((childValue === null || childValue === undefined) && !hasTypeMetadata) {
              continue;
            }

            // Extract template value for this child property
            const childTemplateValue = extractNestedTemplateValue(templateValue, childPath, path);

            const childProperty = createProperty({
              context: {
                path: childPath,
                parentPath: path,
                meta: childMeta,
                store,
              },
              value: childValue,
              onChange,
              depth: depth + 1,
              disabled: params.disabled,
              templateValue: childTemplateValue,
              isOverride: childTemplateValue !== undefined
            });

            if (childProperty) {
              childProperties.push(childProperty);
            }
          }
          property.addProperties(childProperties);
        }
        return property;
      }
      case PropertyType.Array: {
        // Normalize undefined/null values to empty array
        const arrayValue = Array.isArray(value) ? value : [];

        const property = M.ArrayPropertyBuilder.create({
          type: PropertyType.Array,
          ...params,
          value: arrayValue,
        });
        setupProperty(property, onChange, onCreate);

        // Extract child metadata from array metadata if it exists
        // Use getPropertyMetadataArrayMeta for the new PropertyMetadata format
        const arrayMeta = getPropertyMetadataArrayMeta(context.meta);
        const childMeta = arrayMeta?.child;

        // Store context for creating child properties dynamically later
        property.setCreateChildPropertyContext({
          onChange,
          depth,
          isSkipDefaultOnChange,
          childMeta
        });

        // Recursively create child properties for array elements
        if (arrayValue.length > 0) {
          const childProperties: IProperty[] = [];

          for (let i = 0; i < arrayValue.length; i++) {
            const childPath = `${path}[${i}]`;
            const childValue = arrayValue[i];

            // Skip functions to prevent issues
            if (typeof childValue === 'function') {
              continue;
            }

            // Skip null/undefined values ONLY if we don't have type metadata that can create a property
            // Use childMeta directly - do NOT fall back to parent meta as that causes infinite array recursion
            const hasTypeMetadata = childMeta && (
              getPropertyMetadataClassOrAssetType(childMeta) !== undefined ||
              getPropertyMetadataSelectOptions(childMeta) !== undefined
            );

            if ((childValue === null || childValue === undefined) && !hasTypeMetadata) {
              continue;
            }

            // Extract template value for this array element
            const childTemplateValue = extractNestedTemplateValue(templateValue, childPath, path);

            const childProperty = createProperty({
              context: {
                path: childPath,
                parentPath: path,
                meta: childMeta,
                store
              },
              value: childValue,
              onChange,
              depth: depth + 1,
              disabled: params.disabled,
              isSkipDefaultOnChange: isSkipDefaultOnChange,
              templateValue: childTemplateValue,
              isOverride: childTemplateValue !== undefined
            });

            if (childProperty) {
              childProperties.push(childProperty);
            }
          }
          property.setProperties(childProperties);
        }
        return property;
      }
      case PropertyType.Asset: {
        // Extract URL from UrlTexture instances, otherwise use string value
        let assetValue = '';
        if (value instanceof EngineModule.UrlTexture) {
          assetValue = value.url;
        } else if (typeof value === 'string') {
          assetValue = value;
        }

        // Priority: templateValue (prefab) > CDO value > metadata.default > empty string
        let defaultAssetValue = '';
        if (templateValue !== undefined && typeof templateValue === 'string') {
          defaultAssetValue = templateValue;
        } else if (target) {
          const cdo = getDefaultObject(target);
          if (cdo) {
            const cdoValue = get(cdo, path);
            if (typeof cdoValue === 'string') {
              defaultAssetValue = cdoValue;
            } else if (cdoValue instanceof EngineModule.UrlTexture) {
              defaultAssetValue = cdoValue.url;
            }
          }
          if (!defaultAssetValue && meta?.default !== undefined && typeof meta.default === 'string') {
            defaultAssetValue = meta.default;
          }
        } else if (meta?.default !== undefined && typeof meta.default === 'string') {
          defaultAssetValue = meta.default;
        }

        const defaults = { defaultValue: defaultAssetValue, isOverride };
        const property = M.AssetProperty.create({
          type: PropertyType.Asset,
          value: assetValue,
          ...defaults,
          ...params,
          assetTypes: result.assetTypes,
          resourceType: result.resourceType
        });

        // Add custom change handler for texture types to convert URL back to UrlTexture
        if (result.assetTypes?.includes(AssetType.Texture)) {
          property.on('value', createOnChange((target: any, propPath: string, newValue: string) => {
            if (newValue) {
              (target as any)[propPath] = new EngineModule.UrlTexture({ url: newValue });
            } else {
              (target as any)[propPath] = null;
            }
          }));
        }

        if (DEBUG_LOG.assetDefaultValue) console.log('asset default value', label, property.defaultValue, 'value', property.value);
        return setupProperty(property, onChange, onCreate);
      }
      case PropertyType.ActorReference: {
        const defaultObj = templateValue ?? (target ? (get(getDefaultObject(target), path) ?? (meta?.default !== undefined ? meta.default : null)) : (meta?.default !== undefined ? meta.default : null));
        const liveValue = value ?? null;
        const property = M.ActorReferenceProperty.create({
          type: PropertyType.ActorReference,
          valueKey: liveValue?.uuid ?? null,
          defaultValueKey: defaultObj?.uuid ?? null,
          isOverride,
          ...params,
          referenceClass: (meta as any)?.actorClass
        });
        // Populate the volatile live reference without triggering onChange
        if (liveValue) property.setValue(liveValue, false, true);
        return setupProperty(property, onChange, onCreate);
      }
      case PropertyType.ComponentReference: {
        const defaultObj = templateValue ?? (target ? (get(getDefaultObject(target), path) ?? (meta?.default !== undefined ? meta.default : null)) : (meta?.default !== undefined ? meta.default : null));
        const liveValue = value ?? null;
        const property = M.ComponentReferenceProperty.create({
          type: PropertyType.ComponentReference,
          valueKey: liveValue?.uuid ?? null,
          defaultValueKey: defaultObj?.uuid ?? null,
          isOverride,
          ...params,
          referenceClass: (meta as any)?.componentClass
        });
        // Populate the volatile live reference without triggering onChange
        if (liveValue) property.setValue(liveValue, false, true);
        return setupProperty(property, onChange, onCreate);
      }
      case PropertyType.Spawnable: {
        const spawnableValue = typeof value === 'string' ? value : '';
        const defaultValue = getDefaultValue(templateValue, spawnableValue);
        const defaults = { defaultValue: typeof defaultValue === 'string' ? defaultValue : '', isOverride };
        const property = M.SpawnableProperty.create({
          type: PropertyType.Spawnable,
          value: spawnableValue,
          ...defaults,
          ...params,
          spawnableClass: (meta as any)?.spawnableClass
        });
        return setupProperty(property, onChange, onCreate);
      }
      default: {
        console.warn(`[PropertyEditor] Unhandled property type ${result.type} for path ${context.path} with value ${value}`);
      }
    }
  } catch (error) {
    console.error(`[PropertyEditor] Error creating property ${result.type} for path ${context.path} with value ${value}`, error);
  }
};

/** Property setter function type */
export type PropertySetter = (target: any, path: string, value: any, context: ValueChangeContext) => void;

/** Creates an onChange handler with a specified property setter */
export const createOnChange = (customSetPropertyValue: PropertySetter): ValueChangeHandler => {
  return (path, value, context) => {

    const sources = getPropertySources(context.property);
    if (!sources) return;

    const numSources = sources.length;
    for (let i = 0; i < numSources; ++i) {
      // Note: editable or target can be null
      const { editable, target } = sources[i];

      if (context.fromPanel) {
        customSetPropertyValue(target, path, value, context);
      }

      if (!editable) {
        objectActions.notifyEditorPropertyChanged(target, path, value);
      } else if (EditableObject.is(editable)) {
        editable.notifyPropertyChanged(path, value, target);
      }
    }
  };
};


/** Default onChange handler for property updates (sets property/options value) */
export const defaultOnChange: ValueChangeHandler = createOnChange(defaultSetPropertyValue);

/** Helper to create default onChange handler for actor method invocation */
export const invokeOnChange = (methodName: keyof ENGINE.Actor) => createOnChange((target: any, path: string, value: any, context: ValueChangeContext) => {
  invoke(target, methodName, value);
});


/** Helper function to create a deep copy of a value for undo/redo storage */
function createValueCopy(value: any): any {
  if (value === null || value === undefined) return value;

  // Handle THREE.js Vector types
  if (value && typeof value === 'object') {
    if (value.isVector2) return new THREE.Vector2(value.x, value.y);
    if (value.isVector3) return new THREE.Vector3(value.x, value.y, value.z);
    if (value.isVector4) return new THREE.Vector4(value.x, value.y, value.z, value.w);
    if (value.isEuler) return new THREE.Euler(value.x, value.y, value.z, value.order);
    if (value.isColor) return new THREE.Color(value.r, value.g, value.b);
    if (value.isQuaternion) return new THREE.Quaternion(value.x, value.y, value.z, value.w);

    // Handle THREE.js Material types - don't deep copy materials, just store reference
    if (value.isMaterial) return value;

    // Engine Actor / SceneComponent references are identity values — store as-is.
    // We must NOT clone or freeze them; the undo/redo system stores the live
    // reference which is looked up by UUID when the action is replayed.
    if (value instanceof EngineModule.Actor || value instanceof EngineModule.SceneComponent) {
      return value;
    }

    // Handle plain objects and arrays
    if (Array.isArray(value)) return [...value];
    if (value.constructor === Object) return { ...value };
  }

  // Return primitive values as-is
  return value;
}

/**
 * Setter function that uses the new unified setPropertyValue system
 * This function is used by the property editor UI through the createOnChange wrapper
 */
function defaultSetPropertyValue(target: any, path: string, value: any, context: ValueChangeContext) {
  if (DEBUG_LOG.defaultSetPropertyValue) {
    console.log('CALLING defaultSetPropertyValue!!');
  }
  const actionType = context.actionType ?? UndoRedoActionType.Primary;
  setPropertyValue(target, path, value, actionType, context.preDragValue);
};

export function getParentProperty(property: IProperty): Instance<typeof M.ObjectProperty> | undefined {
  return isAlive(property) && hasParentOfType(property, M.ObjectProperty) ? getParentOfType(property, M.ObjectProperty) : undefined;
}

/**
 * getPropertySources
 */
export function getPropertySources(property: IProperty): IPropertySource[] | undefined {
  let sources: IPropertySource[] | undefined;
  // If this is an object property, return it's own sources
  if (M.ObjectProperty.is(property)) {
    sources = (property as Instance<typeof M.ObjectProperty>).sources;
  }
  if (sources) return sources;

  const parent = getParentProperty(property);
  if (parent) {
    sources = (parent as Instance<typeof M.ObjectProperty>).sources;
  }
  if (sources) return sources; // TODO: Both not null

  sources = getPropertyRootSources(property);
  if (sources) return sources;

  return undefined;
}

/**
 * getPropertyRootSources
 */
export function getPropertyRootSources(property: IProperty): IPropertySource[] | undefined {
  return property?.store?.sources;
}

/**
 * New property setting function that uses UniversalAccessor for undo/redo logic.
 * This function leverages the UniversalAccessor's built-in setValue method which handles
 * both engine object updates and editor notifications.
 * Falls back to setPropertyValue for objects without associated actors (e.g., plain THREE.Object3D).
 */
export function setPropertyValue(target: any, path: string, value: any, actionType: UndoRedoActionType = UndoRedoActionType.Primary, preDragValue?: any) {
  if (!target) {
    return;
  }

  // Fast path for Silent actions (e.g. drag): bypass accessor construction, undo/redo
  // history, old-value capture, and inspector panel re-sync entirely. We have the live
  // target in hand, so we write directly using the same copy-method-aware logic.
  if (actionType === UndoRedoActionType.Silent) {
    setPropertyValueDirect(target, path, value);
    return;
  }

  // Create UniversalAccessor from target and path
  const accessor = createAccessorFromTarget(target, path);
  if (accessor.isInvalid) {
    return;
  }

  // Validate property path exists on the target.
  // For nested paths (e.g. "physicsOptions.motionType") we check the root segment ("physicsOptions")
  // rather than the full dotted string, since only the root key can be `in` the target.
  // Also skip the guard for "options.*" and "properties.*" paths which are created dynamically.
  // Use `in` (not `hasOwnProperty`) so prototype-chain getters decorated with @property are found.
  const rootSegment = path.split('.')[0].split('[')[0];
  if (!path.includes('options') && !path.includes('properties') && !(rootSegment in target)) {
    return;
  }

  // Use the pre-drag baseline when provided (drag commit after silent writes have already
  // mutated the engine value), otherwise read the current value from the accessor.
  const oldValue = preDragValue !== undefined ? preDragValue : accessor.resolveProperty();

  // Skip if values are the same (no change needed)
  if (oldValue === value) {
    return;
  }

  // Create copies for undo/redo to avoid reference issues
  // For array values, ensure null/undefined becomes empty array to prevent iteration errors
  const oldValueCopy = Array.isArray(value) && (oldValue === null || oldValue === undefined)
    ? []
    : createValueCopy(oldValue);
  const newValueCopy = createValueCopy(value);

  if (DEBUG_LOG.setPropertyValue) {
    console.log('setPropertyValue', { oldValueCopy, newValueCopy });
  }

  const actionName = `Set ${path}`;

  if (!canRecordPrefabEditorUndo()) {
    accessor.setValue(newValueCopy, actionName);
    return;
  }

  const undoRedo = editorStore.undoRedoManager;
  void undoRedo.recordAction(
    actionName,
    () => { accessor.setValue(newValueCopy, actionName); },
    () => { accessor.setValue(oldValueCopy, actionName); },
    actionType,
  );
}

/**
 * Notify relevant systems about property changes
 */
export function notifyPropertyChange(target: any, path: string, value: any) {
  // Notify other systems about the property change
  // This matches the logic from defaultOnChange but simplified for direct use
  objectActions.notifyEditorPropertyChanged(target, path, value);

  // If target has an associated editable object, notify it
  const firstSelectedObject = editorStore.scene.selectedObjects[0];
  if (firstSelectedObject?.asActor === target || firstSelectedObject?.selectedSubobjectTarget === target) {
    if (firstSelectedObject && EditableObject.is(firstSelectedObject)) {
      firstSelectedObject.notifyPropertyChanged(path, value, target);
    }
  }
}


